/**
 * This file contains type definitions for different types of form fields
 * as they are consumed by e.g. MyForm.vue
 *
 * Currently only 'text', 'editor' ( richer text editor ) and 'switch' field types
 * are supported.
 *
 */

/**
 * String constants used to denote different types of fields
 */
export type FieldType = 'text' | 'switch' | 'editor' | 'isoDate'
/**
 * Field 'values' may be a subset of these
 */
export type FieldValue = string | boolean | undefined | null | Date

// Checks if a value is a js Date obj. Internal helper function.
const isDate = (v: unknown): v is Date => Object.prototype.toString.call(v) === '[object Date]'

type validationFn = (v: unknown) => boolean | string
type validationFns = validationFn[]

/**
 * Shared base props included in all fields
 */
interface FieldBase {
  type: FieldType;
  label: string;
  value?: FieldValue;
  editable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationFns?: ((v: any) => boolean | string)[];
}

/**
 * Form text input field
 */
export interface TextField extends FieldBase {
  type: 'text';
  value?: string | undefined | null;
  placeholder?: string;
}

/**
 * 'Editor' input field
 */
export interface EditorField extends FieldBase {
  type: 'editor';
  value?: string | undefined | null;
  placeholder?: string;
}

/**
 * Form 'switch' field
 */
export interface SwitchField extends FieldBase {
  type: 'switch';
  value?: boolean | undefined | null;
  /**
   * an optional alternative text, that consuming components may choose
   * to display instead of the value true/false (e.g. 'yes' or 'no)¨.
   */
  valueText?: string;
}

/**
 * Date field with string (ISO 8601) representation of the date, i.e.
 * the underlying value is Date but the editing form would display
 * it as a editable text.
 */
export interface IsoDateField extends FieldBase {
  type: 'isoDate';
  value?: Date | null;
  placeholder?: string;
}

type Field = TextField | SwitchField | EditorField | IsoDateField;

/**
 * An object containing different fields, each field identified by
 * a the name of the key
 */
export interface FormFields {
  [key: string]: Field;
  }

/**
 * internal use type type guard. Checks if the input value is FieldType
 * @param f
 */
// eslint-disable-next-line max-len
const isFieldType = (s: string): s is FieldType => (
  s === 'text'
  || s === 'editor'
  || s === 'switch'
  || s === 'isoDate'
)

/**
 * internal use type type guard. Checks if the value is FieldValue
 * @param f
 */
// eslint-disable-next-line max-len
const isFieldValue = (v: unknown): v is FieldValue => (
  typeof v === 'string'
  || typeof v === 'boolean'
  || v === undefined
  || v === null
  || isDate(v)
)

/**
 * Type guard. Checks if the input value is of type Field
 * @param f
 */
export const isFormField = (f: unknown): f is Field => (
  typeof (f as Field).label === 'string')
  && (isFieldType((f as Field).type))
  && (isFieldValue((f as Field).value)
  )

/**
 * Type guard. Checks if the input value is of type TextField
 * @param f
 */
export const isTextField = (f: Field): f is TextField => (typeof f.label === 'string')
  && f.type === 'text'
  && (typeof f.value === 'string' || f.value === undefined || f.value === null)

/**
 * Type guard. Checks if the input value is of type EditorField
 * @param f
 */
export const isEditorField = (f: Field): f is EditorField => (typeof f.label === 'string')
  && f.type === 'editor'
  && (typeof f.value === 'string' || f.value === undefined || f.value === null)

/**
 * Type guard. Checks if the input value is of type SwitchField
 * @param f
 */
export const isSwitchField = (f: Field): f is SwitchField => (typeof f.label === 'string')
  && f.type === 'switch'
  && (typeof f.value === 'boolean' || f.value === undefined || f.value === null)

/**
 * Type guard. Checks if the input value is of type IsoDateField
 * @param f
 */
export const isIsoDateField = (f: Field): f is IsoDateField => (typeof f.label === 'string')
  && f.type === 'isoDate'
  && (isDate(f.value) || f.value === null)

// internal function, checks if the string represents a proper date as iso 8601
const isValidIsoDate = (isoString: unknown) => {
  if (typeof isoString !== 'string') return false
  return !Number.isNaN(Number(Date.parse(isoString)))
}

/**
 * Returns a validation function that checks if the string is a
 * proper iso 8601 date time.
 *
 * @param message
 */
export const isIsoDateValidationFn = (message?: string): validationFn => {
  const myMsg = message
    || 'please enter a valid datetime in iso 8601 format: YYYY-MM-DDTHH:mm:ss.sssZ'
  return (v: unknown) => isValidIsoDate(v as string) || myMsg
}
